import { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { Link, router, usePage } from '@inertiajs/react'
import TypeBadge from './TypeBadge'

const ARCHIVED_BY_LABELS = {
  manual:           "Manual",
  decay:            "Decaimiento",
  stale_synthesis:  "Síntesis obsoleta",
  expiry:           "TTL expirado",
}

const SCOPE_CLASSES = {
  shared:   "bg-emerald-900/40 text-emerald-400 border border-emerald-800/50",
  personal: "bg-violet-900/40 text-violet-400 border border-violet-800/50",
  project:  "bg-zinc-800/60 text-blue-400 border border-zinc-700/50",
}

function ScopeBadge({ scope }) {
  if (!scope) return null
  const cls = SCOPE_CLASSES[scope] ?? "bg-zinc-800/60 text-zinc-400 border border-zinc-700/50"
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-mono ${cls}`}>
      {scope}
    </span>
  )
}

export default function RightPanel({ memory, activeSession, onClose }) {
  const { auth } = usePage().props
  const isOwner = auth?.user?.role === 'owner'
  const [confirming, setConfirming] = useState(false)
  const btnRef = useRef(null)

  useEffect(() => {
    if (!confirming) return
    const handler = (e) => { if (!btnRef.current?.contains(e.target)) setConfirming(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [confirming])

  function handleForget() {
    if (!confirming) { setConfirming(true); return }
    router.patch(`/memories/${memory.id}/archive`, {}, {
      onSuccess: () => { onClose?.(); router.visit('/') }
    })
  }

  if (!memory) return null

  return (
    <aside className="w-80 shrink-0 border-l border-zinc-700 flex flex-col sticky top-0 h-screen overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 shrink-0">
        <Link
          href={`/memories/${memory.id}`}
          className="text-xs font-mono text-zinc-500 hover:text-emerald-300 transition-colors"
        >
          view more →
        </Link>
        <button
          onClick={onClose}
          className="text-zinc-500 hover:text-zinc-200 text-lg leading-none transition-colors"
          aria-label="Close panel"
        >
          ×
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 min-h-0 overflow-hidden px-4 py-3 flex flex-col gap-3">
        {/* Session summary */}
        {activeSession?.summary && (
          <div className="bg-zinc-800/60 border border-zinc-700 rounded p-3 text-xs font-mono text-zinc-400 space-y-1 shrink-0">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">active session</p>
            <p className="text-zinc-300">{activeSession.project_name}</p>
            <p className="text-zinc-500 line-clamp-2">{activeSession.summary}</p>
          </div>
        )}

        {/* Metadata */}
        <div className="space-y-1 shrink-0">
          <h2 className="text-sm font-semibold text-zinc-100 leading-snug line-clamp-2">{memory.title}</h2>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <TypeBadge type={memory.memory_type} />
            <ScopeBadge scope={memory.scope} />
            {memory.project_slug && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-zinc-800/60 text-zinc-400 border border-zinc-700/60">
                {memory.project_slug}
              </span>
            )}
          </div>
          {memory.topic_key && (
            <p className="text-[10px] font-mono text-zinc-600 pt-0.5 truncate">{memory.topic_key}</p>
          )}
        </div>

        {/* Content — truncated, no scroll */}
        <div className="overflow-hidden text-xs font-mono leading-relaxed text-zinc-400 [&_strong]:text-zinc-300 [&_code]:bg-zinc-800 [&_code]:px-1 [&_code]:rounded [&_code]:text-emerald-400 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4">
          <div className="line-clamp-[12]">
            <ReactMarkdown>{memory.content}</ReactMarkdown>
          </div>
        </div>
      </div>

      {/* Footer — timestamp + forget */}
      <div className="shrink-0 px-4 py-2 border-t border-zinc-700 flex items-center justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-mono text-zinc-600">
            actualizado {new Date(memory.updated_at).toLocaleDateString()}
          </span>
          {memory.archived_at && (
            <span className="text-[10px] font-mono text-zinc-500">
              archived {new Date(memory.archived_at).toLocaleDateString()}
              {memory.archived_by && (
                <span className="text-zinc-600"> · {ARCHIVED_BY_LABELS[memory.archived_by] ?? "–"}</span>
              )}
            </span>
          )}
        </div>
        {isOwner && !memory.archived_at && (
          <button
            ref={btnRef}
            onClick={handleForget}
            className={`text-[10px] font-mono rounded px-2 py-1 transition-colors border shrink-0 ${
              confirming
                ? "text-red-400 border-red-800 bg-red-950/40"
                : "text-zinc-600 border-zinc-700 hover:text-zinc-300 hover:border-zinc-500"
            }`}
          >
            {confirming ? "sure?" : "forget"}
          </button>
        )}
      </div>
    </aside>
  )
}
